"use client";

import { useEffect, useState } from "react";
import { isAddress, type Address } from "viem";
import { useReadContracts } from "wagmi";

import { ERC20_ABI } from "@/data/contracts/abis";
import type { TokenInfo } from "@/domain/types";

export interface CustomTokenLookup {
  token: TokenInfo | null;
  /** True while the debounce is pending or the on-chain read is in flight. */
  loading: boolean;
  /** True once a full address has been read and it isn't a working ERC-20. */
  notFound: boolean;
}

/** Debounced (400ms, matching dotone-swap-v3's useTokenField) custom-token
 * metadata lookup for an address the user typed in. */
export function useCustomTokenInfo(input: string): CustomTokenLookup {
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input), 400);
    return () => clearTimeout(t);
  }, [input]);

  const address = isAddress(debounced) ? (debounced as Address) : undefined;
  const { data, isLoading, isFetched } = useReadContracts({
    contracts: address
      ? [
          { address, abi: ERC20_ABI, functionName: "symbol" },
          { address, abi: ERC20_ABI, functionName: "decimals" },
          { address, abi: ERC20_ABI, functionName: "name" },
        ]
      : [],
    query: { enabled: Boolean(address) },
  });

  if (!address) return { token: null, loading: false, notFound: false };
  // Debounce still pending (input hasn't caught up to `debounced` yet) or
  // the on-chain read is in flight.
  if (input !== debounced || isLoading || !isFetched) {
    return { token: null, loading: true, notFound: false };
  }

  const [symbolR, decimalsR, nameR] = data ?? [];
  if (!symbolR || !decimalsR || symbolR.status !== "success" || decimalsR.status !== "success") {
    return { token: null, loading: false, notFound: true };
  }
  const symbol = symbolR.result as string;
  return {
    token: {
      address,
      symbol,
      decimals: decimalsR.result as number,
      name: nameR && nameR.status === "success" ? (nameR.result as string) : symbol,
    },
    loading: false,
    notFound: false,
  };
}
